import { PAGES } from "@dune/types";
import { Button } from "@dune/ui";
import { ArrowLeft } from "@untitled-ui/icons-react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useGetGFACommunications,
	useGetGFADocuments,
	useGetGFAFullDetails,
	useGetGFATasks,
	useGetGFAUnprocessedCommunicationsCount,
} from "../../api/queries/gfa.queries";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutSkeleton,
	TabLayoutErrorSkeleton,
} from "../../component/layout/PageLayoutWithTabs";
import { useRequiredParams } from "../../hooks/useRequiredParams";
import GeneralTab from "../constructionSites/components/tabs/GeneralTab";
import { GFAFinancingTab } from "./components/tabs/GFAFinancingTab";
import { GFAOperationTab } from "./components/tabs/GFAOperationTab";
import { GFAOperatorTab } from "./components/tabs/GFAOperatorTab";
import DocumentsTab from "../constructionSites/components/tabs/DocumentsTab";
import CommunicationsTab from "../constructionSites/components/tabs/CommunicationsTab";
import ErrorBoundary from "../../component/common/ErrorBoundary";
import TasksTab from "../constructionSites/components/tabs/TasksTab";
import { SkippedBanner } from "../constructionSites/components/SkippedBanner";
import { SkipDialogs } from "../constructionSites/components/SkipDialogs";
import { useConstructionSitePageState } from "../constructionSites/hooks/useConstructionSitePageState";

const GFAConstructionSitePage: React.FC = () => {
	const navigate = useNavigate();
	const { id: gfaId } = useRequiredParams<{ id: string }>();

	const [communicationsPage, setCommunicationsPage] = useState(1);
	const [communicationsLimit, setCommunicationsLimit] = useState(10);
	const [communicationsSortBy, setCommunicationsSortBy] =
		useState<string>("sentAt");
	const [communicationsSortOrder, setCommunicationsSortOrder] = useState<
		"asc" | "desc"
	>("desc");

	const {
		data: gfaConstructionSite,
		isLoading,
		error,
		refetch,
	} = useGetGFAFullDetails(gfaId);

	const {
		data: tasksData,
		isLoading: tasksLoading,
		error: tasksError,
	} = useGetGFATasks(gfaId);

	const todoTasksCount = useMemo(() => {
		if (!tasksData) return 0;
		return tasksData.filter((task) => task.status === "TO_DO").length;
	}, [tasksData]);

	const { data: documentsData } = useGetGFADocuments(Number(gfaId), {
		page: 1,
		limit: 1000,
	});
	const toProcessDocumentsCount = useMemo(() => {
		if (!documentsData?.data) return 0;
		return documentsData.data.filter((doc) => doc.status === "TO_BE_PROCESSED")
			.length;
	}, [documentsData?.data]);
	const {
		data: communicationsData,
		isLoading: communicationsLoading,
		error: communicationsError,
	} = useGetGFACommunications(gfaId, {
		page: communicationsPage,
		limit: communicationsLimit,
		sortBy: communicationsSortBy,
		sortOrder: communicationsSortOrder,
	});

	const { data: unprocessedCommunicationsCount = 0 } =
		useGetGFAUnprocessedCommunicationsCount(gfaId);

	useConstructionSitePageState({
		entityType: "GFA",
		entityId: Number(gfaId),
		communications: communicationsData?.data || [],
		tasks: tasksData || [],
		emailRoutingUuid: gfaConstructionSite?.emailRoutingUuid || undefined,
		emailPrefix: "chantierGFA",
	});

	const tabs: Tab[] = [
		{ value: "general", label: "Général" },
		{ value: "operation", label: "Opération" },
		{ value: "operator", label: "Opérateur" },
		{ value: "financing", label: "Financement" },
		{ value: "tarification", label: "Tarification" },
		{
			value: "documents",
			label: "Documents",
			notificationCount: toProcessDocumentsCount,
		},
		{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
		{
			value: "communications",
			label: "Communications",
			notificationCount: unprocessedCommunicationsCount,
		},
		{
			value: "activites",
			label: "Activités",
		},
	];

	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault("general"),
	);

	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !gfaConstructionSite) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations du chantier."
				tabCount={tabs.length}
			/>
		);
	}

	const tabContent: { [key: string]: React.ReactNode } = {
		general: <GeneralTab constructionSite={gfaConstructionSite} />,
		operation: <GFAOperationTab gfa={gfaConstructionSite} />,
		operator: <GFAOperatorTab gfa={gfaConstructionSite} />,
		financing: <GFAFinancingTab gfa={gfaConstructionSite} />,
		taches: (
			<ErrorBoundary fallbackMessage="Erreur lors du chargement des tâches.">
				<TasksTab
					tasks={tasksData || []}
					isLoading={tasksLoading}
					error={tasksError}
				/>
			</ErrorBoundary>
		),
		documents: <DocumentsTab constructionSiteId={gfaId} isGFA />,
		communications: (
			<CommunicationsTab
				constructionSiteId={gfaId}
				communicationsResponse={communicationsData}
				isLoading={communicationsLoading}
				error={communicationsError}
				onPageChange={setCommunicationsPage}
				onSortChange={(sortBy, sortOrder) => {
					setCommunicationsSortBy(sortBy);
					setCommunicationsSortOrder(sortOrder);
				}}
				onLimitChange={setCommunicationsLimit}
				isGFA
				emailRoutingUuid={gfaConstructionSite?.emailRoutingUuid}
			/>
		),
	};

	return (
		<div className="flex flex-col h-full">
			{gfaConstructionSite.isSkipped && (
				<SkippedBanner skipReason={gfaConstructionSite.skipReason} />
			)}
			<div className="flex-1">
				<PageLayoutWithTabs
					title={getConstructionSiteTitle(gfaConstructionSite.id)}
					tabs={tabs}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					tabContent={tabContent}
					topActions={
						<div className="flex items-center justify-between gap-2">
							<Button
								variant="ghost"
								onClick={() => navigate(PAGES.PROJECTS.index)}
								className="has-[>svg]:px-1"
							>
								<ArrowLeft />
								Retour
							</Button>
							<div className="flex items-center gap-2">
								<SkipDialogs
									entityId={String(gfaConstructionSite.id)}
									isSkipped={gfaConstructionSite.isSkipped}
									onSuccess={refetch}
								/>
							</div>
						</div>
					}
				/>
			</div>
		</div>
	);
};

export default GFAConstructionSitePage;

export const getConstructionSiteTitle = (constructionSiteId: number) => {
	return `CH-${constructionSiteId.toString().padStart(4, "0")}`;
};
